import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

import theme from '../../constants/theme';
import { SCREEN_WIDTH } from '../../utils/screensize';
import Text from '../global/Text';

type AttachmentKind = 'video' | 'document';

type SelectedAttachmentTileProps = {
  kind: AttachmentKind;
  fileName: string;
  onPress?: () => void;
  onActionPress?: () => void;
  actionAccessibilityLabel?: string;
  accessibilityLabel?: string;
  testID?: string;
};

const TILE_CONFIG = {
  video: {
    iconName: 'video-outline',
    iconColor: theme.colors.primary,
    iconBackground: '#ECFDF3',
    badgeBackground: '#ECFDF3',
    badgeTextColor: '#027A48',
    fallbackBadge: 'VIDEO',
    indicatorIconName: 'pencil-outline'
  },
  document: {
    iconName: 'file-document-outline',
    iconColor: theme.colors.primary,
    iconBackground: '#ECFDF3',
    badgeBackground: '#ECFDF3',
    badgeTextColor: '#027A48',
    fallbackBadge: 'FILE',
    indicatorIconName: 'chevron-right'
  }
};

export class SelectedAttachmentTile extends React.Component<SelectedAttachmentTileProps> {
  getFileExtensionLabel = (fileName: string, fallback: string) => {
    const trimmed = (fileName || '').trim();
    if (!trimmed) {
      return fallback;
    }

    const extension = trimmed.split('.').pop();
    if (!extension || extension === trimmed) {
      return fallback;
    }

    return extension.toUpperCase();
  };

  renderContent = () => {
    const { fileName, kind, onActionPress, actionAccessibilityLabel } = this.props;
    const tileConfig = TILE_CONFIG[kind] || TILE_CONFIG.document;
    const extensionLabel = this.getFileExtensionLabel(fileName, tileConfig.fallbackBadge);

    return (
      <View style={styles.card}>
        <View style={[styles.iconContainer, { backgroundColor: tileConfig.iconBackground }]}>
          <Icon color={tileConfig.iconColor} name={tileConfig.iconName} size={22} />
        </View>

        <View style={styles.contentContainer}>
          <Text allowFontScaling={false} ellipsizeMode="middle" numberOfLines={1} style={styles.fileName}>
            {fileName}
          </Text>
          <View style={styles.metaRow}>
            <View style={[styles.badge, { backgroundColor: tileConfig.badgeBackground }]}>
              <Text
                allowFontScaling={false}
                numberOfLines={1}
                style={[styles.badgeText, { color: tileConfig.badgeTextColor }]}
              >
                {extensionLabel}
              </Text>
            </View>
          </View>
        </View>

        {onActionPress ? (
          <TouchableOpacity
            accessibilityLabel={actionAccessibilityLabel}
            accessibilityRole="button"
            hitSlop={{ bottom: 8, left: 8, right: 8, top: 8 }}
            onPress={onActionPress}
            style={styles.actionButton}
          >
            <Icon color={tileConfig.iconColor} name="pencil-outline" size={18} />
          </TouchableOpacity>
        ) : (
          <View style={styles.indicatorButton}>
            <Icon color={tileConfig.iconColor} name={tileConfig.indicatorIconName} size={22} />
          </View>
        )}
      </View>
    );
  };

  render() {
    const { accessibilityLabel, onPress, testID } = this.props;

    if (!onPress) {
      return (
        <View style={styles.wrapper} testID={testID}>
          {this.renderContent()}
        </View>
      );
    }

    return (
      <TouchableOpacity
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button"
        activeOpacity={0.84}
        onPress={onPress}
        style={styles.wrapper}
        testID={testID}
      >
        {this.renderContent()}
      </TouchableOpacity>
    );
  }
}

const styles = StyleSheet.create({
  wrapper: {
    width: SCREEN_WIDTH > 500 ? 360 : SCREEN_WIDTH - 92,
    marginVertical: 6,
    marginHorizontal: 4
  },
  card: {
    minHeight: 74,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#D0D5DD',
    backgroundColor: '#FFFFFF',
    shadowColor: '#101828',
    shadowOffset: {
      width: 0,
      height: 3
    },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2
  },
  iconContainer: {
    width: 42,
    height: 42,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12
  },
  contentContainer: {
    flex: 1,
    minWidth: 0
  },
  fileName: {
    fontSize: 14,
    lineHeight: 20,
    color: '#344054'
  },
  metaRow: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center'
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999
  },
  badgeText: {
    fontSize: 10,
    lineHeight: 12,
    letterSpacing: 0.4
  },
  actionButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F2F4F7',
    marginLeft: 12
  },
  indicatorButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    marginLeft: 12
  }
});

export default SelectedAttachmentTile;
